/**
 * SequencerLaneEditor — modal for one lane's metadata, plus the
 * sequencer-level step count (it's the most natural place to expose
 * it: same surface where the user edits per-lane note/channel).
 *
 * Opened by long-pressing a lane label in SequencerGrid. Edits:
 *   - label (free text)
 *   - kind: Note | CC | Light. Light (2026-09-28) points the lane at one
 *     fixture or zone and one thing it controls (`LaneLightPicker`); the
 *     step's velocity becomes that value. Switching kinds keeps the other
 *     kind's settings in the draft, so flipping back restores them.
 *   - channel (1..16 in UI, 0..15 in storage)
 *   - note / cc number (0..127) — for noteon, shows the GM drum name
 *     when one matches (helps when programming kit lanes)
 *   - steps (1..64) — applies to the WHOLE sequencer
 *
 * Draft-clone-on-open pattern, same as MacrosEditor: edits go into
 * local signals; Save commits via `patchLane` + `patchSequencer`,
 * Cancel discards. Esc and click-outside both cancel.
 */

import { createSignal, createMemo, Show } from 'solid-js';
import { isDmxLaneTarget, type LaneDmxTarget, type SequencerMidiKind, type SequencerMidiTarget } from '@opbuttons/schema';
import { findSequencer, patchLane, patchSequencer } from '../sequencer/store.ts';
import { LaneLightPicker, defaultLaneDmxTarget } from './LaneLightPicker.tsx';

interface SequencerLaneEditorProps {
  sequencerId: string;
  laneId: string;
  onClose: () => void;
}

/** General MIDI percussion map (channel 10). Only the common ones —
 *  enough to label the defaults the drum factory ships with. Falls
 *  through to "Note N" for non-percussion or unknown numbers. */
const GM_DRUMS: Record<number, string> = {
  35: 'Acoustic Bass Drum',
  36: 'Kick',
  37: 'Side Stick',
  38: 'Snare',
  39: 'Hand Clap',
  40: 'Electric Snare',
  41: 'Low Floor Tom',
  42: 'Closed Hat',
  43: 'High Floor Tom',
  44: 'Pedal Hat',
  45: 'Low Tom',
  46: 'Open Hat',
  47: 'Low-Mid Tom',
  48: 'Hi-Mid Tom',
  49: 'Crash 1',
  50: 'High Tom',
  51: 'Ride 1',
  52: 'China',
  53: 'Ride Bell',
  54: 'Tambourine',
  55: 'Splash',
  56: 'Cowbell',
  57: 'Crash 2',
  59: 'Ride 2',
};

/** Note number → label like "C1" (MIDI convention with C-1 = note 0).
 *  Useful when the user is targeting a melodic instrument lane. */
function noteName(n: number): string {
  if (n < 0 || n > 127) return String(n);
  const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const octave = Math.floor(n / 12) - 1;
  return `${names[n % 12]}${octave}`;
}

export function SequencerLaneEditor(props: SequencerLaneEditorProps) {
  const sequencer = createMemo(() => findSequencer(props.sequencerId));
  const lane = createMemo(() => sequencer()?.lanes.find((l) => l.id === props.laneId) ?? null);

  // Draft state. Initialise from the lane on open; if the lane disappears
  // mid-edit (unlikely — user would have to dismiss the modal) the
  // signals retain their last values until Save/Cancel.
  const initialSeq = sequencer();
  const initialLane = lane();
  const [label, setLabel] = createSignal(initialLane?.label ?? '');
  const initialTarget = initialLane?.target;
  const initialMidi: SequencerMidiTarget | undefined =
    initialTarget && !isDmxLaneTarget(initialTarget) ? initialTarget : undefined;
  const [kind, setKind] = createSignal<SequencerMidiKind | 'light'>(
    initialTarget && isDmxLaneTarget(initialTarget) ? 'light' : initialMidi?.kind ?? 'noteon',
  );
  const [channel1, setChannel1] = createSignal((initialMidi?.channel ?? 0) + 1);
  const [number, setNumber] = createSignal(initialMidi?.number ?? 36);
  const [light, setLight] = createSignal<LaneDmxTarget | null>(
    initialTarget && isDmxLaneTarget(initialTarget) ? initialTarget : null,
  );
  /** No light patched at all - Light cannot be offered, and says why. */
  const noLights = () => light() === null && defaultLaneDmxTarget() === null;
  function chooseLight(): void {
    if (light() === null) setLight(defaultLaneDmxTarget());
    if (light() !== null) setKind('light');
  }
  const [lengthSteps, setLengthSteps] = createSignal(initialSeq?.lengthSteps ?? 16);

  const numberLabel = createMemo(() => {
    if (kind() === 'cc') return `CC ${number()}`;
    const drum = GM_DRUMS[number()];
    return drum ? `${number()} · ${drum}` : `${number()} · ${noteName(number())}`;
  });

  function save() {
    const ch = Math.max(1, Math.min(16, channel1() | 0)) - 1;
    const num = Math.max(0, Math.min(127, number() | 0));
    const len = Math.max(1, Math.min(64, lengthSteps() | 0));
    const cur = lane();
    const curSeq = sequencer();
    if (!cur || !curSeq) { props.onClose(); return; }
    const trimmed = label().trim();
    const k = kind();
    const lit = light();
    const lanePatch: Parameters<typeof patchLane>[2] = {
      target: k === 'light' && lit
        ? lit
        : {
            bridge: isDmxLaneTarget(cur.target) ? 'all' : cur.target.bridge,
            kind: k === 'light' ? 'noteon' : k,
            channel: ch,
            number: num,
          },
    };
    if (trimmed) lanePatch.label = trimmed;
    patchLane(props.sequencerId, props.laneId, lanePatch);
    if (len !== curSeq.lengthSteps) {
      patchSequencer(props.sequencerId, { lengthSteps: len });
    }
    props.onClose();
  }

  function onBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) props.onClose();
  }
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); props.onClose(); }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
  }

  return (
    <div class="lane-editor-backdrop" onClick={onBackdrop} onKeyDown={onKeyDown}>
      <div class="lane-editor" role="dialog" aria-label="Edit lane">
        <header class="lane-editor-head">
          <h2>Lane</h2>
          <div class="lane-editor-head-actions">
            <button type="button" class="lane-editor-cancel" onClick={() => props.onClose()}>
              Cancel
            </button>
            <button type="button" class="lane-editor-save" onClick={save}>
              Save
            </button>
          </div>
        </header>

        <div class="lane-editor-body">
          <Show when={lane()} fallback={<div class="lane-editor-missing">Lane no longer exists.</div>}>
            <label class="lane-editor-row">
              <span class="lane-editor-label">Label</span>
              <input
                type="text"
                class="lane-editor-input"
                value={label()}
                placeholder={`Lane`}
                maxLength={24}
                onInput={(e) => setLabel(e.currentTarget.value)}
              />
            </label>

            <label class="lane-editor-row">
              <span class="lane-editor-label">Kind</span>
              <div class="lane-editor-segments">
                <button
                  type="button"
                  class="lane-editor-segment"
                  data-active={kind() === 'noteon' ? 'true' : 'false'}
                  onClick={() => setKind('noteon')}
                >
                  Note
                </button>
                <button
                  type="button"
                  class="lane-editor-segment"
                  data-active={kind() === 'cc' ? 'true' : 'false'}
                  onClick={() => setKind('cc')}
                >
                  CC
                </button>
                <button
                  type="button"
                  class="lane-editor-segment"
                  data-kind="light"
                  data-active={kind() === 'light' ? 'true' : 'false'}
                  disabled={noLights()}
                  onClick={chooseLight}
                  title={noLights() ? 'No lights patched yet' : 'Drive a light instead of MIDI'}
                >
                  Light
                </button>
              </div>
            </label>
            <Show when={noLights()}>
              <p class="lane-editor-note">To drive a light from this lane, add one in Lights first.</p>
            </Show>

            <Show when={kind() === 'light' && light()}>
              {(t) => (
                <>
                  <LaneLightPicker
                    target={t()}
                    onChange={setLight}
                    rowClass="lane-editor-row"
                    labelClass="lane-editor-label"
                    selectClass="lane-editor-input"
                  />
                  <p class="lane-editor-note">Each lit step sets the light by its velocity: 127 is full, 1 is barely on. It holds that until the next lit step, and still holds it after the sequencer stops, so end the pattern where you want the light left.</p>
                </>
              )}
            </Show>

            <Show when={kind() !== 'light'}>

            <label class="lane-editor-row">
              <span class="lane-editor-label">Channel</span>
              <div class="lane-editor-stepper">
                <button
                  type="button"
                  class="lane-editor-step-btn"
                  onClick={() => setChannel1((c) => Math.max(1, c - 1))}
                  aria-label="Decrement channel"
                >−</button>
                <input
                  type="number"
                  class="lane-editor-input lane-editor-input-num"
                  min={1}
                  max={16}
                  value={channel1()}
                  aria-label="Channel"
                  onInput={(e) => {
                    const v = parseInt(e.currentTarget.value, 10);
                    if (Number.isFinite(v)) setChannel1(Math.max(1, Math.min(16, v)));
                  }}
                />
                <button
                  type="button"
                  class="lane-editor-step-btn"
                  onClick={() => setChannel1((c) => Math.min(16, c + 1))}
                  aria-label="Increment channel"
                >+</button>
              </div>
            </label>

            <label class="lane-editor-row">
              <span class="lane-editor-label">{kind() === 'cc' ? 'CC #' : 'Note'}</span>
              <div class="lane-editor-stepper">
                <button
                  type="button"
                  class="lane-editor-step-btn"
                  onClick={() => setNumber((n) => Math.max(0, n - 1))}
                  aria-label="Decrement number"
                >−</button>
                <input
                  type="number"
                  class="lane-editor-input lane-editor-input-num"
                  min={0}
                  max={127}
                  value={number()}
                  aria-label={kind() === 'cc' ? 'CC number' : 'Note number'}
                  onInput={(e) => {
                    const v = parseInt(e.currentTarget.value, 10);
                    if (Number.isFinite(v)) setNumber(Math.max(0, Math.min(127, v)));
                  }}
                />
                <button
                  type="button"
                  class="lane-editor-step-btn"
                  onClick={() => setNumber((n) => Math.min(127, n + 1))}
                  aria-label="Increment number"
                >+</button>
              </div>
              <span class="lane-editor-hint">{numberLabel()}</span>
            </label>
            </Show>

            <label class="lane-editor-row">
              <span class="lane-editor-label">Steps</span>
              <div class="lane-editor-stepper">
                <button
                  type="button"
                  class="lane-editor-step-btn"
                  onClick={() => setLengthSteps((n) => Math.max(1, n - 1))}
                  aria-label="Decrement length"
                >−</button>
                <input
                  type="number"
                  class="lane-editor-input lane-editor-input-num"
                  min={1}
                  max={64}
                  value={lengthSteps()}
                  aria-label="Sequencer step count"
                  onInput={(e) => {
                    const v = parseInt(e.currentTarget.value, 10);
                    if (Number.isFinite(v)) setLengthSteps(Math.max(1, Math.min(64, v)));
                  }}
                />
                <button
                  type="button"
                  class="lane-editor-step-btn"
                  onClick={() => setLengthSteps((n) => Math.min(64, n + 1))}
                  aria-label="Increment length"
                >+</button>
              </div>
              <span class="lane-editor-hint">whole sequencer</span>
            </label>

            <p class="lane-editor-note">
              Step count applies to every lane in this sequencer. Changing it keeps existing steps; new slots start off, dropped slots are discarded.
            </p>
          </Show>
        </div>
      </div>
    </div>
  );
}
